import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode } from "vue"

const _hoisted_1 = { key: 0 }
const _hoisted_2 = { class: "rounded-2xl bg-rose-50 ring-1 ring-rose-200 p-4 sm:p-5" }
const _hoisted_3 = { class: "flex items-start gap-3" }
const _hoisted_4 = { class: "shrink-0 inline-flex size-9 items-center justify-center rounded-xl bg-white text-rose-600 ring-1 ring-rose-200" }
const _hoisted_5 = { class: "min-w-0 flex-1" }
const _hoisted_6 = { class: "text-sm font-bold text-gray-900" }
const _hoisted_7 = { class: "mt-1 text-sm text-gray-700" }
const _hoisted_8 = { class: "mt-3 flex flex-wrap items-center gap-2" }
const _hoisted_9 = { class: "text-xs text-gray-500" }
const _hoisted_10 = ["aria-label"]
const _hoisted_11 = { class: "w-full max-w-md rounded-2xl bg-white shadow-xl ring-1 ring-gray-200 p-6" }
const _hoisted_12 = { class: "inline-flex size-11 items-center justify-center rounded-xl bg-rose-50 text-rose-600 ring-1 ring-rose-200" }
const _hoisted_13 = { class: "mt-4 text-lg font-bold text-gray-900" }
const _hoisted_14 = { class: "mt-2 text-sm text-gray-600" }
const _hoisted_15 = { class: "mt-4 space-y-2 rounded-xl bg-gray-50 ring-1 ring-gray-100 p-3" }
const _hoisted_16 = { class: "flex items-center gap-2 text-xs text-gray-700" }
const _hoisted_17 = { class: "flex items-center gap-2 text-xs text-gray-700" }
const _hoisted_18 = { class: "mt-5 flex flex-col-reverse sm:flex-row sm:justify-end gap-2" }

import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import {
  ExclamationTriangleIcon, CheckCircleIcon, XCircleIcon,
} from '@heroicons/vue/24/outline';
import usePluginStore from '@/store/plugin/plugin';
import Plugin from '@/models/plugin/plugin';
import { buildInstagramOauthUrl, newOauthState } from '@/utils/instagramOauth';


export default /*@__PURE__*/_defineComponent({
  __name: 'PluginConnectionAlert',
  props: {
    plugin: {},
    showModal: { type: Boolean }
  },
  setup(__props: any) {

const props = __props;

const { t } = useI18n();
const pluginStore = usePluginStore();

const needsReconnect = computed(() => {
  const p = props.plugin;
  // `needsReconnect` lives on the model so every surface agrees on what
  // "broken" means. Guarded because layouts seed `plugin` with `{} as Plugin`
  // before the fetch resolves, so the method may genuinely be absent.
  return !!p && typeof p.needsReconnect === 'function' && p.needsReconnect();
});

// Keyed per plugin: a seller with two accounts should be warned about each.
const seenKey = computed(() => `livelink_reconnect_seen_${props.plugin?.id || ''}`);
const dismissed = ref(false);

const modalOpen = computed(() => {
  if (!props.showModal || !needsReconnect.value || dismissed.value) return false;
  try {
    return sessionStorage.getItem(seenKey.value) !== '1';
  } catch {
    // Private mode / storage disabled — show it. Better one extra prompt
    // than silently swallowing the only warning she gets.
    return true;
  }
});

// Reset when the seller switches account so the new plugin gets its own
// evaluation instead of inheriting the previous one's dismissal.
watch(() => props.plugin?.id, () => { dismissed.value = false; });

function dismissModal() {
  dismissed.value = true;
  try {
    sessionStorage.setItem(seenKey.value, '1');
  } catch { /* storage unavailable — it just reappears next navigation */ }
}

function reconnect() {
  const state = newOauthState();
  pluginStore.csrf = state;
  window.location.href = buildInstagramOauthUrl(state);
}

return (_ctx: any,_cache: any) => {
  return (needsReconnect.value)
    ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
        _createElementVNode("div", _hoisted_2, [
          _createElementVNode("div", _hoisted_3, [
            _createElementVNode("span", _hoisted_4, [
              _createVNode(_unref(ExclamationTriangleIcon), { class: "size-5" })
            ]),
            _createElementVNode("div", _hoisted_5, [
              _createElementVNode("h3", _hoisted_6, _toDisplayString(_unref(t)('plugins.connectionAlert.title')), 1),
              _createElementVNode("p", _hoisted_7, _toDisplayString(_unref(t)('plugins.connectionAlert.body')), 1),
              _createElementVNode("div", _hoisted_8, [
                _createElementVNode("button", {
                  type: "button",
                  class: "rounded-xl bg-teal-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-teal-700 transition",
                  onClick: reconnect
                }, _toDisplayString(_unref(t)('plugins.connectionAlert.cta')), 1),
                _createElementVNode("span", _hoisted_9, _toDisplayString(_unref(t)('plugins.connectionAlert.reassurance')), 1)
              ])
            ])
          ])
        ]),
        (modalOpen.value)
          ? (_openBlock(), _createElementBlock("div", {
              key: 0,
              class: "fixed inset-0 z-[80] flex items-center justify-center bg-gray-900/50 p-4",
              role: "dialog",
              "aria-modal": "true",
              "aria-label": _unref(t)('plugins.connectionAlert.title')
            }, [
              _createElementVNode("div", _hoisted_11, [
                _createElementVNode("span", _hoisted_12, [
                  _createVNode(_unref(ExclamationTriangleIcon), { class: "size-6" })
                ]),
                _createElementVNode("h3", _hoisted_13, _toDisplayString(_unref(t)('plugins.connectionAlert.title')), 1),
                _createElementVNode("p", _hoisted_14, _toDisplayString(_unref(t)('plugins.connectionAlert.body')), 1),
                _createElementVNode("ul", _hoisted_15, [
                  _createElementVNode("li", _hoisted_16, [
                    _createVNode(_unref(CheckCircleIcon), { class: "size-4 shrink-0 text-emerald-600" }),
                    _createTextVNode(" " + _toDisplayString(_unref(t)('plugins.connectionAlert.stillWorks')), 1)
                  ]),
                  _createElementVNode("li", _hoisted_17, [
                    _createVNode(_unref(XCircleIcon), { class: "size-4 shrink-0 text-rose-600" }),
                    _createTextVNode(" " + _toDisplayString(_unref(t)('plugins.connectionAlert.stopped')), 1)
                  ])
                ]),
                _createElementVNode("div", _hoisted_18, [
                  _createElementVNode("button", {
                    type: "button",
                    class: "rounded-xl px-5 py-2.5 text-sm font-semibold text-gray-700 ring-1 ring-gray-300 hover:bg-gray-50 transition",
                    onClick: dismissModal
                  }, _toDisplayString(_unref(t)('plugins.connectionAlert.later')), 1),
                  _createElementVNode("button", {
                    type: "button",
                    class: "rounded-xl bg-teal-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-teal-700 transition",
                    onClick: reconnect
                  }, _toDisplayString(_unref(t)('plugins.connectionAlert.cta')), 1)
                ])
              ])
            ], 8, _hoisted_10))
          : _createCommentVNode("", true)
      ]))
    : _createCommentVNode("", true)
}
}

})